export type OpsEnvironment = 'dev' | 'staging' | 'production';
export type OpsRuntime = 'no-docker' | 'docker' | 'k8s';

export type ServiceId =
  | 'backoffice_api'
  | 'backoffice_fo'
  | 'copilot_fo'
  | 'copilot_api'
  | 'analytics_hub_fo'
  | 'analytics_hub_api'
  | 'orchestration_fo'
  | 'orchestration_api'
  | 'orchestration_worker';

export interface ServiceDef {
  id: ServiceId;
  name: string;
  short: string;
  defaultPort: number;
}

export const SERVICE_CATALOG: ServiceDef[] = [
  { id: 'backoffice_api', name: 'Backoffice API', short: 'BO API', defaultPort: 7152 },
  { id: 'backoffice_fo', name: 'Backoffice Portal', short: 'BO FO', defaultPort: 3000 },
  { id: 'copilot_fo', name: 'Copilot Frontend', short: 'Copilot FO', defaultPort: 5176 },
  { id: 'copilot_api', name: 'Copilot API', short: 'Copilot API', defaultPort: 8006 },
  { id: 'analytics_hub_fo', name: 'Analytics Hub Frontend', short: 'AH FO', defaultPort: 5175 },
  { id: 'analytics_hub_api', name: 'Analytics Hub API', short: 'AH API', defaultPort: 8012 },
  { id: 'orchestration_fo', name: 'Orchestration Hub Frontend', short: 'Orch FO', defaultPort: 5174 },
  { id: 'orchestration_api', name: 'Orchestration Studio API', short: 'Orch API', defaultPort: 8000 },
  { id: 'orchestration_worker', name: 'Orchestration Worker', short: 'Worker', defaultPort: 8100 },
];

export type ServicePathsMap = Record<ServiceId, string>;

export interface SecretsConfig {
  postgres: string;
  redis: string;
  mongo: string;
  temporalHost: string;
  temporalNamespace: string;
  rabbitmq: string;
  logs: string;
}

export const OPS_PASSWORD = 'admin123';
export const UNLOCK_KEY = 'ops_console_unlocked';
export const ENV_KEY = 'ops_console_env';
export const RUNTIME_KEY = 'ops_console_runtime';

const PATH_ROOT: Record<OpsEnvironment, string> = {
  dev: 'D:\\Office\\Companies\\SelficAI',
  staging: 'D:\\Office\\Companies\\SelficAI-staging',
  production: 'D:\\Office\\Companies\\SelficAI-prod',
};

const FOLDER: Record<ServiceId, string> = {
  backoffice_api: 'backend\\backoffice-api',
  backoffice_fo: 'frontend\\agenticapp',
  copilot_fo: 'frontend\\copilot-fo',
  copilot_api: 'backend\\copilot-api',
  analytics_hub_fo: 'frontend\\analytics-hub-fo',
  analytics_hub_api: 'backend\\analytics-hub-api',
  orchestration_fo: 'frontend\\orchestration-fo',
  orchestration_api: 'backend\\orchestration-api',
  orchestration_worker: 'backend\\orchestration-worker',
};

function dockerUrl(env: OpsEnvironment, port: number, path = '/'): string {
  const host =
    env === 'dev' ? '127.0.0.1' : env === 'staging' ? 'staging.selficai.local' : 'prod.selficai.local';
  const proto = env === 'dev' ? 'http' : 'https';
  return `${proto}://${host}:${port}${path}`;
}

function k8sUrl(env: OpsEnvironment, service: ServiceId): string {
  const ns = env === 'production' ? 'selfic-prod' : env === 'staging' ? 'selfic-staging' : 'selfic-dev';
  const map: Record<ServiceId, string> = {
    backoffice_api: `https://portalapi.${env === 'production' ? 'selficai.com' : `${env}.selficai.com`}/`,
    backoffice_fo: `https://portal.${env === 'production' ? 'selficai.com' : `${env}.selficai.com`}/`,
    copilot_fo: `https://copilot.${env === 'production' ? 'selficai.com' : `${env}.selficai.com`}/`,
    copilot_api: `https://copilotapi.${env === 'production' ? 'selficai.com' : `${env}.selficai.com`}/`,
    analytics_hub_fo: `https://analytics.${env === 'production' ? 'selficai.com' : `${env}.selficai.com`}/`,
    analytics_hub_api: `https://analyticshubapi.${env === 'production' ? 'selficai.com' : `${env}.selficai.com`}/`,
    orchestration_fo: `https://orcv2.${env === 'production' ? 'selficai.com' : `${env}.selficai.com`}/`,
    orchestration_api: `https://orcv2api.${env === 'production' ? 'selficai.com' : `${env}.selficai.com`}/`,
    orchestration_worker: `http://orchestration-worker.${ns}.svc.cluster.local:8100/health`,
  };
  return map[service];
}

export function seedPaths(env: OpsEnvironment, runtime: OpsRuntime): ServicePathsMap {
  const out = {} as ServicePathsMap;
  for (const svc of SERVICE_CATALOG) {
    if (runtime === 'no-docker') {
      out[svc.id] = `${PATH_ROOT[env]}\\${FOLDER[svc.id]}`;
    } else if (runtime === 'docker') {
      out[svc.id] = dockerUrl(env, svc.defaultPort);
    } else {
      out[svc.id] = k8sUrl(env, svc.id);
    }
  }
  return out;
}

export function seedSecrets(env: OpsEnvironment): SecretsConfig {
  if (env === 'dev') {
    return {
      postgres: 'Host=127.0.0.1;Port=5432;Database=selfic_dev;Username=selfic;Password=dev_postgres_pass',
      redis: 'redis://127.0.0.1:6379/0',
      mongo: 'mongodb://selfic:dev_mongo_pass@127.0.0.1:27017/selfic_dev?authSource=admin',
      temporalHost: '127.0.0.1:7233',
      temporalNamespace: 'default',
      rabbitmq: 'amqp://guest:guest@127.0.0.1:5672/',
      logs: 'http://127.0.0.1:9200',
    };
  }
  if (env === 'staging') {
    return {
      postgres: 'Host=staging-pg.selficai.internal;Port=5432;Database=selfic_staging;Username=selfic_app;Password=stg_***_redacted',
      redis: 'rediss://staging-redis.selficai.internal:6380/1',
      mongo: 'mongodb+srv://selfic_stg:***@staging-mongo.selficai.internal/selfic_staging',
      temporalHost: 'temporal.staging.selficai.internal:7233',
      temporalNamespace: 'selfic-staging',
      rabbitmq: 'amqps://selfic_stg:***@rabbit.staging.selficai.internal:5671/',
      logs: 'https://logs.staging.selficai.internal:9200',
    };
  }
  return {
    postgres: 'Host=prod-pg.selficai.internal;Port=5432;Database=selfic_prod;Username=selfic_app;Password=prod_***_redacted',
    redis: 'rediss://prod-redis.selficai.internal:6380/0',
    mongo: 'mongodb+srv://selfic_prod:***@prod-mongo.selficai.internal/selfic_prod',
    temporalHost: 'temporal.prod.selficai.internal:7233',
    temporalNamespace: 'selfic-prod',
    rabbitmq: 'amqps://selfic_prod:***@rabbit.prod.selficai.internal:5671/',
    logs: 'https://logs.selficai.com:9200',
  };
}

export function pathsStorageKey(env: OpsEnvironment, runtime: OpsRuntime): string {
  return `ops_console_paths_${env}_${runtime}`;
}

export function secretsStorageKey(env: OpsEnvironment): string {
  return `ops_console_secrets_${env}`;
}

export function syntheticLogLines(service: ServiceDef, action: 'start' | 'stop' | 'health'): string[] {
  const ts = () => new Date().toISOString().replace('T', ' ').slice(0, 23);
  if (action === 'stop') {
    return [
      `${ts()} [INFO]  Shutting down ${service.name}…`,
      `${ts()} [INFO]  Graceful stop complete (exit 0)`,
    ];
  }
  if (action === 'health') {
    return [`${ts()} [INFO]  Health probe OK · :${service.defaultPort}/health`];
  }
  return [
    `${ts()} [INFO]  Bootstrapping ${service.name} (${service.id})`,
    `${ts()} [INFO]  Loading configuration profile`,
    `${ts()} [INFO]  Binding http://0.0.0.0:${service.defaultPort}`,
    `${ts()} [INFO]  Ready — listening on :${service.defaultPort}`,
  ];
}
